import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { breadcrumbSchema } from "@/components/Seo";
import PageSeo from "@/components/PageSeo";
import PageHeader from "@/components/public/PageHeader";
import { Spinner } from "@/components/public/Primitives";
import { useLanguage } from "@/context/LanguageContext";
import { getFacts } from "@/lib/api";

export default function Facts() {
  const { data: facts = [], isLoading } = useQuery({ queryKey: ["facts"], queryFn: getFacts });
  const { tr } = useLanguage();

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: facts.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  return (
    <div data-testid="facts-page">
      <PageSeo
        slug="facts"
        title="Facts About Girish Kumar Naidu | Q&A"
        description="Key facts about Girish Kumar Naidu — who he is, his social work, charitable trust, awards, leadership roles, and vision for Hoodi Ward 29."
        keywords="Who is Girish Kumar Naidu, Girish Kumar Naidu facts, social worker Bengaluru, GBA Hoodi Ward 29 aspirant"
        schemas={[faqSchema, breadcrumbSchema([{ name: "Home", url: window.location.origin }, { name: "Facts" }])]}
      />
      <PageHeader
        overline={tr("page.facts.overline")}
        title={tr("page.facts.title")}
        subtitle={tr("page.facts.subtitle")}
        crumbs={[{ name: "Facts" }]}
      />

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          {isLoading ? (
            <Spinner />
          ) : (
            <Accordion type="single" collapsible className="space-y-4" data-testid="facts-accordion">
              {facts.map((f, i) => (
                <AccordionItem key={f.id} value={f.id} className="border border-gray-200 rounded-2xl px-6 overflow-hidden bg-paper data-[state=open]:border-gold/40 transition-colors">
                  <AccordionTrigger className="text-left font-display text-base md:text-lg font-bold text-navy hover:no-underline py-5 gap-4">
                    <span className="flex items-start gap-4">
                      <span className="font-mono text-xs text-gold font-bold mt-1.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      {f.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed pb-5 pl-9">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </section>
    </div>
  );
}
